/**
 * Product Detail — drill-in for one product across all banks (PHASE2_SPEC §4.10).
 *
 * Route: `/products/:productCode?type=LOAN`
 * Layout:
 *   Breadcrumb · Header (code + name + type)
 *   KPI tiles (banks · accounts · exposure ₹Cr · NPA %)
 *   Bar chart: top 20 banks by exposure
 *   Two tables: per-bank breakdown · per-branch breakdown (cap at 50)
 */

import React, { useEffect, useMemo } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import BankBadge from '../../components/BankBadge';
import BarChart from '../../components/BarChart';
import Card from '../../components/Card';
import EmptyState from '../../components/EmptyState';
import ErrorBanner from '../../components/ErrorBanner';
import KpiTile from '../../components/KpiTile';
import LoadingSkeleton from '../../components/LoadingSkeleton';
import SortableTh from '../../components/SortableTh';
import StatusPill from '../../components/StatusPill';
import { formatDate } from '../../helpers/dateFormat';
import { npaFlag } from '../../helpers/flag';
import { formatAbsCrore, formatCount, formatCrore, formatPct } from '../../helpers/format';
import { useSortable } from '../../hooks/useSortable';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { fetchProductDetail, selectProductDetail } from '../../redux/slices/dashboard';
import type {
	ProductDetailByBankRow,
	ProductDetailByBranchRow,
	ProductType,
} from '../../type/api';

function isProductType(s: string | null): s is ProductType {
	return s === 'LOAN' || s === 'DEPOSIT' || s === 'OTHER';
}

export default function ProductDetailPage() {
	const params = useParams<{ productCode: string }>();
	const [searchParams] = useSearchParams();
	const productCode = params.productCode || '';
	const typeQS = searchParams.get('type');
	const type: ProductType = isProductType(typeQS) ? typeQS : 'LOAN';

	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const cell = useAppSelector(selectProductDetail(type, productCode));

	useEffect(() => {
		if (!productCode) return;
		// Always fetch on mount/key change so back nav re-fetches reliably
		// only if the cache is empty.
		if (cell.status === 'idle') {
			dispatch(fetchProductDetail({ productCode, type }));
		}
	}, [cell.status, dispatch, productCode, type]);

	const data = cell.data;

	const bankRows: ProductDetailByBankRow[] = useMemo(
		() => (Array.isArray(data?.byBank) ? data!.byBank : []),
		[data],
	);
	const branchRows: ProductDetailByBranchRow[] = useMemo(
		() => (Array.isArray(data?.byBranch) ? data!.byBranch : []),
		[data],
	);

	const bankSort = useSortable<ProductDetailByBankRow>(bankRows, { field: 'amountCr', dir: 'desc' });
	const branchSort = useSortable<ProductDetailByBranchRow>(branchRows, { field: 'amountCr', dir: 'desc' });

	const handleRetry = () => dispatch(fetchProductDetail({ productCode, type }));

	if (cell.status === 'loading' && !data) {
		return (
			<div className="page-body">
				<div className="page-head"><div><h1 className="page-title">Product</h1></div></div>
				<Card><LoadingSkeleton rows={5} columns={5} /></Card>
			</div>
		);
	}
	if (cell.status === 'error') {
		return (
			<div className="page-body">
				<div className="page-head"><div><h1 className="page-title">Product</h1></div></div>
				<ErrorBanner message={cell.error || 'Failed to load product'} onRetry={handleRetry} />
			</div>
		);
	}
	if (!data) {
		return (
			<div className="page-body">
				<div className="page-head"><div><h1 className="page-title">Product</h1></div></div>
				<Card><EmptyState title="No data for this product" /></Card>
			</div>
		);
	}

	const product = data.product;
	const summary = data.summary;
	const top20 = bankSort.rows.slice(0, 20).map((b) => ({
		name: b.orgName.length > 16 ? b.orgName.slice(0, 16) + '…' : b.orgName,
		value: Math.abs(b.amountCr),
	}));

	return (
		<div className="page-body">
			<div className="breadcrumb">
				<Link to="/products">Product Analytics</Link>
				<span className="sep">/</span>
				<span>{product.productCode}</span>
			</div>
			<div className="page-head">
				<div>
					<h1 className="page-title">{product.productName}</h1>
					<div className="page-sub">
						Code <b style={{ color: 'var(--text)' }}>{product.productCode}</b> ·{' '}
						<span className="chip muted">{product.productType}</span>
						{data.asOf && (
							<>
								{' · As of '}
								<span style={{ color: 'var(--text-dim)', fontWeight: 500 }}>
									{formatDate(data.asOf)}
								</span>
							</>
						)}
					</div>
				</div>
				<button type="button" className="btn ghost" onClick={() => navigate('/products')}>← All products</button>
			</div>

			<div className="grid cols-4">
				<KpiTile label="Banks Holding" value={formatCount(summary.bankCount)} />
				<KpiTile label="Total Accounts" value={formatCount(summary.totalAccountCount)} />
				<KpiTile label="Exposure" value={formatCrore(summary.totalAmountCr, 2)} primary />
				<KpiTile
					label={type === 'LOAN' ? 'NPA %' : 'NPA ₹Cr'}
					value={type === 'LOAN' ? formatPct(summary.npaPct) : formatCrore(summary.npaCr, 2)}
					flag={type === 'LOAN' ? npaFlag(summary.npaPct) : undefined}
					flagVariant="npa"
				/>
			</div>

			<Card title="Top 20 banks by exposure (₹ Cr)">
				<BarChart data={top20} valueFormatter={(v) => formatCrore(v, 1)} />
			</Card>

			<Card title={`Per-bank breakdown (${bankRows.length})`}>
				<div style={{ overflowX: 'auto' }}>
					<table className="tbl" style={{ width: '100%' }}>
						<thead>
							<tr>
								<SortableTh field="orgId" sort={bankSort} numeric>ID</SortableTh>
								<SortableTh field="orgName" sort={bankSort}>Bank</SortableTh>
								<SortableTh field="accountCount" sort={bankSort} numeric>Accounts</SortableTh>
								<SortableTh field="amountCr" sort={bankSort} numeric>Amount ₹Cr</SortableTh>
								<SortableTh field="npaCr" sort={bankSort} numeric>NPA ₹Cr</SortableTh>
								<SortableTh field="npaPct" sort={bankSort} numeric>NPA %</SortableTh>
							</tr>
						</thead>
						<tbody>
							{bankSort.rows.map((b) => (
								<tr
									key={b.orgId}
									className="clickable"
									onClick={() => navigate(`/banks/${b.orgId}`)}
									tabIndex={0}
								>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>{b.orgId}</td>
									<td><BankBadge orgId={b.orgId} orgName={b.orgName} /></td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatCount(b.accountCount)}</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatAbsCrore(b.amountCr)}</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>
										{type === 'LOAN' ? formatAbsCrore(b.npaCr) : '—'}
									</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>
										{type === 'LOAN' ? (
											<StatusPill flag={npaFlag(b.npaPct)} variant="npa" compact label={formatPct(b.npaPct)} />
										) : (
											<span style={{ color: 'var(--text-faint)' }}>—</span>
										)}
									</td>
								</tr>
							))}
							{!bankSort.rows.length && (
								<tr><td colSpan={6}><EmptyState title="No banks hold this product" /></td></tr>
							)}
						</tbody>
					</table>
				</div>
			</Card>

			<Card title={`Per-branch breakdown (top ${branchRows.length})`}>
				<div style={{ overflowX: 'auto' }}>
					<table className="tbl" style={{ width: '100%' }}>
						<thead>
							<tr>
								<SortableTh field="orgName" sort={branchSort}>Bank</SortableTh>
								<SortableTh field="branchIdLocal" sort={branchSort}>Branch ID</SortableTh>
								<SortableTh field="branchName" sort={branchSort}>Branch</SortableTh>
								<SortableTh field="accountCount" sort={branchSort} numeric>Accounts</SortableTh>
								<SortableTh field="amountCr" sort={branchSort} numeric>Amount ₹Cr</SortableTh>
							</tr>
						</thead>
						<tbody>
							{branchSort.rows.map((br) => (
								<tr
									key={`${br.orgId}-${br.branchIdLocal}`}
									className="clickable"
									onClick={() => navigate(`/banks/${br.orgId}/branches/${encodeURIComponent(br.branchIdLocal)}`)}
									tabIndex={0}
								>
									<td><BankBadge orgId={br.orgId} orgName={br.orgName} /></td>
									<td style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{br.branchIdLocal}</td>
									<td>{br.branchName}</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatCount(br.accountCount)}</td>
									<td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatAbsCrore(br.amountCr)}</td>
								</tr>
							))}
							{!branchSort.rows.length && (
								<tr><td colSpan={5}><EmptyState title="No branches" /></td></tr>
							)}
						</tbody>
					</table>
				</div>
			</Card>
		</div>
	);
}
